import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { faClipboard, faCommentSlash, faEnvelope, faGlobe, faPhone } from '@fortawesome/pro-light-svg-icons';
import { TranslocoService } from '@ngneat/transloco';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { AcModel } from 'projects/runner/src/app/models/ac.model';
import { CandidateModel } from 'projects/runner/src/app/models/candidate.model';
import { ObserverModel } from 'projects/runner/src/app/models/observer.model';
import { ReportModel } from 'projects/runner/src/app/models/report.model';
import { UserModel } from 'projects/runner/src/app/models/user.model';
import { ApiService } from 'projects/runner/src/app/services/api.service';
import { AppService } from 'projects/runner/src/app/services/app.service';
import { AuthStateService } from 'projects/runner/src/app/services/auth-state.service';
import { DataService } from 'projects/runner/src/app/services/data.service';
import { TimezoneService } from 'projects/runner/src/app/services/timezone.service';
import { UiService } from 'projects/runner/src/app/services/ui.service';

@Component({
  selector: 'app-participant',
  templateUrl: './participant.component.html',
  styleUrls: ['./participant.component.scss']
})
export class ParticipantComponent implements OnInit {

  @ViewChild('fileInput') fileInput: ElementRef;

  @Input() user: CandidateModel | ObserverModel | UserModel;
  @Input() ac: AcModel;
  @Input() report: ReportModel | null = null;

  @Output() reportChange = new EventEmitter<ReportModel>();

  public items: Array<MenuItem> = [];
  public timezoneSelectorVisible = false;
  public infoModeratorVisible = false;
  public candiateToMove = null;

  public faClipboard = faClipboard;
  public faGlobe = faGlobe;
  public faEnvelope = faEnvelope;
  public faCommentSlash = faCommentSlash;
  public faPhone = faPhone;

  constructor(
    private transloco: TranslocoService,
    private ui: UiService,
    private api: ApiService,
    private app: AppService,
    private data: DataService,
    private confirmation: ConfirmationService,
    public timezone: TimezoneService,
    public auth: AuthStateService
  ) { }

  ngOnInit(): void {
    this.initMenu();
  }

  public openTimezoneSelector(): void {
    this.timezoneSelectorVisible = true;
  }

  public onReportChanged(report: ReportModel): void {
    this.reportChange.emit(report);
  }

  private initMenu(): void {
    this.items = [];
    if (this.auth.isModerator() && this.user?.role !== 'moderator') {
      this.items.push({
        label: this.transloco.translate('dashboard.misc.copydirecturl'),
        icon: 'pi pi-copy',
        command: () => {
          this.ui.copyToClipboard(this.user?.directlink);
        }
      });
      if (this.ac?.isGroup() && this.user.role === 'candidate' && this.user instanceof CandidateModel) {
        this.items.push({
          label: this.transloco.translate('dashboard.misc.move_to_other_ac'),
          icon: 'pi pi-sign-out',
          command: () => {
            this.candiateToMove = this.user as CandidateModel;
          }
        });
      }
    }
    if (this.auth.isMe(this.user?.hashed_id)) {
      this.items.push({
        label: this.transloco.translate('dashboard.misc.change_timezone'),
        icon: 'pi pi-globe',
        command: () => {
          this.openTimezoneSelector();
        }
      });
      this.items.push({
        label: this.transloco.translate((this.user.avatar !== null) ? 'dashboard.misc.change_avatar' : 'dashboard.misc.add_avatar'),
        icon: 'pi pi-user',
        command: () => {
          this.fileInput.nativeElement.click();
        }
      });
      if (this.user.avatar !== null) {
        this.items.push({
          label: this.transloco.translate('dashboard.misc.delete_avatar'),
          icon: 'pi pi-trash',
          styleClass: 'danger-button',
          command: () => {
            this.onDeleteAvatar();
          }
        });
      }
    }
  }

  public onMoveCancel(): void {
    this.candiateToMove = null;
  }

  public onMoveDone(): void {
    this.candiateToMove = null;
  }

  public onUploadAvatar(files: FileList): void {
    if (files.length > 0) {
      this.api.uploadAvatar(files).subscribe({
        next: (result: any) => {
          this.user.avatar = result.data;
          this.data.setAvatar(result.data);
          this.initMenu();
          this.fileInput.nativeElement.value = '';
        },
        error: (error: any) => {
          this.ui.toastError(this.transloco.translate('partials.file_handling.upload_error'));
        }
      });
    }
  }

  public onDeleteAvatar(): void {
    this.confirmation.confirm({
      message: this.transloco.translate('dashboard.misc.really_delete_avatar'),
      header: this.transloco.translate('dashboard.misc.delete_avatar'),
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'pi pi-trash',
      acceptLabel: this.transloco.translate('common.labels.delete'),
      rejectIcon: '',
      rejectLabel: this.transloco.translate('common.labels.cancel'),
      accept: () => {
        this.api.deleteAvatar().subscribe({
          next: () => {
            this.user.avatar = null;
            this.data.setAvatar('DELETED');
            this.initMenu();
            this.ui.toastSuccess(this.transloco.translate('dashboard.misc.avatar_successfully_deleted'));
          },
          error: () => {
            this.ui.toastError(this.transloco.translate('dashboard.misc.error_deleting_avatar'));
          }
        });
      }
    });
  }

  public getInfos(): Array<{icon: string, label: string, value: string}> {
    return [
      {
        icon: this.user?.isOnline() ? 'pi pi-wifi' : 'pi pi-link',
        label: this.transloco.translate('common.labels.status'),
        value: this.transloco.translate(this.user?.isOnline() ? 'common.labels.online' : 'common.labels.offline')
      },
      {
        icon: 'pi pi-globe',
        label: this.transloco.translate('common.labels.timezone'),
        value: this.timezone.getTimezoneString(this.user.hashed_id, true)
      }
    ];
  }

  showModeratorInfo(): void {
    this.infoModeratorVisible = true;
  }

}
